// Always call the production backend URL in all environments
// since the backend is never run locally.
const API_BASE_URL = 'https://trade.botmatic.site/botmaticbinance';

// API service class
class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Generic request method
  async makeRequest(endpoint, method = 'POST', data = null) {
    try {
      const url = `${this.baseURL}${endpoint}`;
      const options = {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
      };

      if (data) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(url, options);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // User authentication
  async verifyUserDetails(credentials) {
    const userData = {
      eMailId: credentials.username,
      password: credentials.password,
    };
    
    const response = await this.makeRequest('/user/verifyUserDetails', 'POST', userData);
    
    // Log the response for debugging
    console.log('Login API Response:', response);
    
    return response;
  }

  // Create new user
  async createUser(userData) {
    return this.makeRequest('/user/createUser', 'POST', userData);
  }

  // Get user details
  async getUserDetails(userId) {
    const data = { userId };
    return this.makeRequest('/user/getUserDetails', 'POST', data);
  }

  // Update user details
  async updateUserDetails(userData) {
    console.log('API Service - Updating user with data:', userData);
    const response = await this.makeRequest('/user/updateUserDetails', 'POST', userData);
    console.log('API Service - Update response:', response);
    return response;
  }

  // Get users list
  async getUsersList(filters = {}) {
    console.log('API Service - getUsersList called with filters:', filters);
    const response = await this.makeRequest('/user/getUsersList', 'POST', filters);
    console.log('API Service - getUsersList response:', response);
    return response;
  }

  // Get scoped users list (self + descendants) or all if admin
  async getScopedUsersList(userId) {
    return this.makeRequest('/user/getScopedUsersList', 'POST', { userId });
  }

  // Trading Account APIs
  async createTradingAccount(tradingAccountData) {
    return this.makeRequest('/user/createTradingAccount', 'POST', tradingAccountData);
  }

  async getTradingAccountList(filters = {}) {
    return this.makeRequest('/user/getTradingAccountList', 'POST', filters);
  }

  async getTradingAccountListAll(filters = {}) {
    return this.makeRequest('/user/getTradingAccountListAll', 'POST', filters);
  }

  async deleteTradingAccount(tradingAccountData) {
    return this.makeRequest('/user/deleteTradingAccount', 'POST', tradingAccountData);
  }

  // Symbol APIs
  async createSymbol(symbolData) {
    return this.makeRequest('/user/createSymbol', 'POST', symbolData);
  }

  async updateSymbol(symbolData) {
    return this.makeRequest('/user/updateSymbol', 'POST', symbolData);
  }

  async getSymbolList(filters = {}) {
    return this.makeRequest('/user/getSymbolList', 'POST', filters);
  }

  async getSymbolListAll(filters = {}) {
    return this.makeRequest('/user/getSymbolListAll', 'POST', filters);
  }

  // Symbol utilities for select-by-symbol
  async getUniqueSymbols() {
    return this.makeRequest('/user/getUniqueSymbols', 'POST');
  }

  async getUsersBySymbol(symbolName) {
    return this.makeRequest('/user/getUsersBySymbol', 'POST', { symbolName });
  }

  async deleteSymbol(symbolData) {
    return this.makeRequest('/user/deleteSymbol', 'POST', symbolData);
  }

  // User Tree
  async getUserTree() {
    return this.makeRequest('/user/getUserTree', 'POST', {});
  }

  // Symbol limits (min order qty, max leverage)
  async getSymbolLimits({ symbol, accountId }) {
    return this.makeRequest('/user/getSymbolLimits', 'POST', { symbol, accountId });
  }

  // Trades: Open positions for an account (uses that account's API keys)
  async getOpenPositions(tradingAccount) {
    return this.makeRequest('/user/getOpenPositions', 'POST', tradingAccount);
  }

  // Trades: Open orders for an account
  async getOpenOrders(tradingAccount) {
    return this.makeRequest('/user/getOpenOrders', 'POST', tradingAccount);
  }

  // Trades: Order history (all orders)
  async getOrderHistory(tradingAccount) {
    return this.makeRequest('/user/getOrderHistory', 'POST', tradingAccount);
  }

  // Trades: Transaction history (income)
  async getTransactionHistory(tradingAccount) {
    return this.makeRequest('/user/getTransactionHistory', 'POST', tradingAccount);
  }

  // Trades: Funding fee history
  async getFundingFeeHistory({ accountId, startTimeMs, endTimeMs }) {
    return this.makeRequest('/user/getFundingFeeHistory', 'POST', { accountId, startTimeMs, endTimeMs });
  }

  // Trades: Position history (last 50) for an account
  async getPositionHistory({ accountId, startTimeMs, endTimeMs } ) {
    return this.makeRequest('/user/getPositionHistory', 'POST', { accountId, startTimeMs, endTimeMs });
  }

  // Trade Logs with filters
  async getTradeLogs(filters = {}) {
    // filters: { userId?, accountId?, symbolName? }
    return this.makeRequest('/user/getTradeLogs', 'POST', filters);
  }

  // Dashboard stats
  async getDashboardStats({ userId, accountId, startTimeMs, endTimeMs } = {}) {
    return this.makeRequest('/user/getDashboardStats', 'POST', { userId, accountId, startTimeMs, endTimeMs });
  }

  // Positions status (from DB) for a user
  async getPositionsStatus(userId) {
    return this.makeRequest('/user/getPositionsStatus', 'POST', { userId });
  }

  // Current loss (DB) for a user
  async getCurrentLoss(userId) {
    return this.makeRequest('/user/getCurrentLoss', 'POST', { userId });
  }

  // Account info (alias and available balance) using API keys
  async getAccountInfo({ binanceFuturesApiKey, binanceFuturesSecretKey }) {
    return this.makeRequest('/user/getAccountInfo', 'POST', { binanceFuturesApiKey, binanceFuturesSecretKey });
  }

  // Technicals: Symbol screener snapshots
  async getSymbolScreenerSnapshot({ date, hour }) {
    const params = new URLSearchParams({
      date,
      hour: String(hour),
    }).toString();
    return this.makeRequest(`/screener/symbols?${params}`, 'GET');
  }

  async getTrendingSymbolSnapshot({ date, hour }) {
    const params = new URLSearchParams({
      date,
      hour: String(hour),
    }).toString();
    return this.makeRequest(`/screener/trending?${params}`, 'GET');
  }

  // Technicals: daily trending snapshots
  // Current trending does not take a date parameter (server chooses appropriate date)
  async getCurrentTrending() {
    return this.makeRequest('/screener/currentTrending', 'GET');
  }

  async triggerCurrentTrendingRanking() {
    return this.makeRequest('/screener/triggerRanking', 'POST');
  }

  async deleteCurrentTrending(symbol) {
    return this.makeRequest(`/screener/currentTrending/${encodeURIComponent(symbol)}`, 'DELETE');
  }

  async getDeletedTrending({ date }) {
    const params = new URLSearchParams({ date }).toString();
    return this.makeRequest(`/screener/deletedTrending?${params}`, 'GET');
  }

  // Backtest: signal-receiver flow backtest
  async runBacktestFlow(payload) {
    // payload should match FlowBacktestRequest:
    // { symbol, daysBack, timeFrame, samplingPeriod, rangeMultiplier,
    //   initialSizeUsdt, tp1Percent, tp2Percent, slPercent, waitFailures, verbose }
    return this.makeRequest('/backtest/flow', 'POST', payload);
  }

  // Backtest: signal-receiver flow backtest (doubling strategy)
  async runBacktestFlowDouble(payload) {
    return this.makeRequest('/backtest/flowDouble', 'POST', payload);
  }

  // Simulator: Simulated positions
  async getSimulatedPositions({ strategyId, symbol }) {
    const params = new URLSearchParams({ strategyId: String(strategyId) });
    if (symbol) params.append('symbol', symbol);
    return this.makeRequest(`/simTrade/positions?${params.toString()}`, 'GET');
  }

  // Simulator: Trade history
  async getSimulatedHistory({ strategyId, symbol }) {
    const params = new URLSearchParams({ strategyId: String(strategyId) });
    if (symbol) params.append('symbol', symbol);
    return this.makeRequest(`/simTrade/history?${params.toString()}`, 'GET');
  }

  // Simulator: Trade logs
  async getSimulatedLogs({ strategyId, symbol }) {
    const params = new URLSearchParams({ strategyId: String(strategyId) });
    if (symbol) params.append('symbol', symbol);
    return this.makeRequest(`/simTrade/logs?${params.toString()}`, 'GET');
  }

  // Simulator: Current loss
  async getSimulatedCurrentLoss({ strategyId, symbol }) {
    const params = new URLSearchParams({ strategyId: String(strategyId) });
    if (symbol) params.append('symbol', symbol);
    return this.makeRequest(`/simTrade/currentLoss?${params.toString()}`, 'GET');
  }

  // Simulator: Maximum drawdown report (no params)
  async getSimulatedDrawdown({ strategyId }) {
    const params = new URLSearchParams({ strategyId: String(strategyId) });
    return this.makeRequest(`/simTrade/drawdownReport?${params.toString()}`, 'GET');
  }

  // Simulator: Signals (strategy-scoped, symbol optional)
  async getAllGeneratedSignals({ strategyId, symbol }) {
    const params = new URLSearchParams({ strategyId: String(strategyId) });
    if (symbol) params.append('symbol', symbol);
    return this.makeRequest(`/screener/allGeneratedSignals?${params.toString()}`, 'GET');
  }

  // Strategies
  async getStrategies() {
    return this.makeRequest('/strategy/list', 'GET');
  }

  async getStrategyById(strategyId) {
    return this.makeRequest(`/strategy/${encodeURIComponent(strategyId)}`, 'GET');
  }

  async getStrategyReportOverview({ strategyId, hoursBack = 48, tradesLimit = 200 }) {
    const params = new URLSearchParams({
      strategyId: String(strategyId),
      hoursBack: String(hoursBack),
      tradesLimit: String(tradesLimit),
    }).toString();
    return this.makeRequest(`/strategyReport/overview?${params}`, 'GET');
  }

  async getStrategyCandles({ strategyId, symbol, interval = '1m', limit = 500 }) {
    const params = new URLSearchParams({
      strategyId: String(strategyId),
      symbol,
      interval,
      limit: String(limit),
    }).toString();
    return this.makeRequest(`/chart/candles?${params}`, 'GET');
  }

  // Simulator: Refresh positions
  async refreshSimulatedPositions({ strategyId, symbol }) {
    const params = new URLSearchParams({ strategyId: String(strategyId) });
    if (symbol) params.append('symbol', symbol);
    return this.makeRequest(`/simTrade/refreshPositions?${params.toString()}`, 'GET');
  }

  // Simulator 10s: Symbols list (current trending symbols in 10s table)
  async getSimulated10sSymbols({ page = 0 } = {}) {
    const params = new URLSearchParams({ page: String(page) }).toString();
    return this.makeRequest(`/simTrade10s/symbols?${params}`, 'GET');
  }

  // Simulator 10s: Simulated positions (symbol required)
  async getSimulated10sPositions({ symbol, page = 0 }) {
    const params = new URLSearchParams({ symbol: String(symbol || ''), page: String(page) }).toString();
    return this.makeRequest(`/simTrade10s/positions?${params}`, 'GET');
  }

  // Simulator 10s: Refresh positions (symbol required)
  async refreshSimulated10sPositions({ symbol, page = 0 }) {
    const params = new URLSearchParams({ symbol: String(symbol || ''), page: String(page) }).toString();
    return this.makeRequest(`/simTrade10s/refreshPositions?${params}`, 'GET');
  }

  // Simulator 10s: Trade history (symbol required)
  async getSimulated10sHistory({ symbol, page = 0 }) {
    const params = new URLSearchParams({ symbol: String(symbol || ''), page: String(page) }).toString();
    return this.makeRequest(`/simTrade10s/history?${params}`, 'GET');
  }

  // Simulator 10s: Trade logs (symbol required)
  async getSimulated10sLogs({ symbol, page = 0 }) {
    const params = new URLSearchParams({ symbol: String(symbol || ''), page: String(page) }).toString();
    return this.makeRequest(`/simTrade10s/logs?${params}`, 'GET');
  }

  // Simulator 10s: Current loss (symbol required)
  async getSimulated10sCurrentLoss({ symbol, page = 0 }) {
    const params = new URLSearchParams({ symbol: String(symbol || ''), page: String(page) }).toString();
    return this.makeRequest(`/simTrade10s/currentLoss?${params}`, 'GET');
  }

  // Simulator 10s: Maximum drawdown report (symbol required)
  async getSimulated10sDrawdown({ symbol, page = 0 }) {
    const params = new URLSearchParams({ symbol: String(symbol || ''), page: String(page) }).toString();
    return this.makeRequest(`/simTrade10s/drawdownReport?${params}`, 'GET');
  }

  // CurrentTrendingSymbol10s CRUD
  async getCurrentTrending10sList({ page = 0 } = {}) {
    const params = new URLSearchParams({ page: String(page) }).toString();
    return this.makeRequest(`/currentTrending10s/list?${params}`, 'GET');
  }

  async getCurrentTrending10sBySymbol(symbol) {
    return this.makeRequest(`/currentTrending10s/${encodeURIComponent(String(symbol || ''))}`, 'GET');
  }

  async addCurrentTrending10s(body) {
    return this.makeRequest('/currentTrending10s/add', 'POST', body);
  }

  async updateCurrentTrending10s(symbol, body) {
    return this.makeRequest(`/currentTrending10s/update/${encodeURIComponent(String(symbol || ''))}`, 'PUT', body);
  }

  async removeCurrentTrending10s(symbol) {
    return this.makeRequest(`/currentTrending10s/remove/${encodeURIComponent(String(symbol || ''))}`, 'DELETE');
  }

  // Symbol info
  async getSymbolInfo(symbol) {
    const params = new URLSearchParams({ symbol }).toString();
    return this.makeRequest(`/tradeBinance/symbolInfo?${params}`, 'GET');
  }

  // Symbol market data
  async getSymbolMarketData(symbol) {
    const params = new URLSearchParams({ symbol }).toString();
    return this.makeRequest(`/tradeBinance/marketData?${params}`, 'GET');
  }

  // Blacklist
  async getBlacklist() {
    return this.makeRequest('/blacklist/list', 'GET');
  }

  async addBlacklist({ symbol, reason }) {
    return this.makeRequest('/blacklist/add', 'POST', { symbol, reason });
  }

  async removeBlacklist(symbol) {
    return this.makeRequest(`/blacklist/remove/${encodeURIComponent(symbol)}`, 'DELETE');
  }

  // Close a specific position
  async closePosition({ symbol, positionSide, quantity, binanceFuturesApiKey, binanceFuturesSecretKey }) {
    const res = await this.makeRequest('/user/closePosition', 'POST', { symbol, positionSide, quantity, binanceFuturesApiKey, binanceFuturesSecretKey });
    if (res && res.errorCode && res.errorCode !== 200) {
      throw new Error(res.errorMessage || 'Failed to close position');
    }
    return res;
  }

  // Close all positions for an account
  async closeAllPositions({ binanceFuturesApiKey, binanceFuturesSecretKey }) {
    return this.makeRequest('/user/closeAllPositions', 'POST', { binanceFuturesApiKey, binanceFuturesSecretKey });
  }
}

// Create and export API service instance
const apiService = new ApiService();
export default apiService;
